import React, { useState, useEffect } from 'react';
import Link from '@mui/material/Link';
import {
  Box,
  Typography,
  CircularProgress,
  Fade,
  Card,
  CardHeader,
  CardActions,
  CardContent,
  Divider,
  Stack,
  Chip,
  Checkbox,
  Tooltip,
  TextField,
  InputAdornment,
  Avatar,
  Paper,
} from '@mui/material';
import MaterialSymbol from '../../components/MaterialSymbol';
import { IconButton, Collapse } from '@mui/material';
import KeyboardDoubleArrowDownIcon from '@mui/icons-material/KeyboardDoubleArrowDown';
import EditTwoToneIcon from '@mui/icons-material/EditTwoTone';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import AddCircleIcon from '@mui/icons-material/AddCircle';
// import SendIcon from '@mui/icons-material/Send';

import Button from '@mui/material/Button';
import Title from '../../components/Title';
import SearchBar from '../../components/SearchBar';
import EditModal from '../../components/EditModal';
import MemberOnboardModal from '../../components/MemberOnboardModal';
import MemberRenewModal from '../../components/MemberRenewModal';
import MemberPaymentConfirmationModal from '../../components/MemberPaymentConfirmationModal';
import MemberJourneyModal from '../../components/MemberJourneyModal';
import AssignMultipleModal from '../../components/AssignMultipleModal';
import UploadModal from '../../components/UploadModal';
import DeleteConfirmationDialog from '../../components/DeleteConfirmationDialog';
import { ProfileAvatar } from '../../components/DynamicForm';
import { humanReadableDate, formatCurrency, titleCase, fitnessPaymentStatusColor, FITNESS_PAYMENT_ITEM_TYPE_LABEL } from '../../common/AppUtils';
import { httpRequest } from '../../api/helper';
import { useNotification } from '../../context/NotificationContext';
import { API_ERROR_MESSAGE, NA_LABEL, SEND_INVITE_URL } from '../../common/AppConstants';

const TITLE = 'Payments';
const API_URL = 'fitness-memberships/payments/';

const NO_DATA_AVAILABLE_STRING =
  'No data available, please start adding members !!!';


export default function FitnessPayments() {
  const { addNotification } = useNotification();
  const [isLoading, setIsLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');

  // Filtered data based on search
  const filteredRows = rows.filter(row_item =>
    row_item.member_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    row_item.payment_mode?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    row_item.status?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleFilterApply = async () => {
    // ToDo: Filter table data
  };

  useEffect(() => {
    const fetchData = async () => {
      refreshData();
    };
    fetchData();
  }, []);

  const refreshData = async (event) => {
    setIsLoading(true);

    await httpRequest({
      method: 'GET',
      url: API_URL,
      onSuccess: (data) => {
        // setColumns(data.columns);
        setRows(data);
      },
      onFailure: (data) => {
        // setColumns([]);
        setRows([]);
      },
      addNotification,
    });
    
    setIsLoading(false);
  };


  const PaymentCard = ({ payment_row, payment_dict, relatedData }) => {
    const [expanded, setExpanded] = useState(false);  // incomplete_data
    const chipColor = fitnessPaymentStatusColor(payment_dict?.status);

    const handleExpandClick = () => {
      setExpanded((prev) => !prev);
    };


    return (
      <Card
        variant="outlined"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          borderRadius: 2,
          transition: 'box-shadow 0.3s',
          '&:hover': { boxShadow: 3 }
        }}
      >
        {/* Header */}
        <CardHeader
          avatar={
            <ProfileAvatar dataDict={payment_dict} handleSendInviteModalOpen={undefined} />
          }
          title={
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              {/* <Typography variant="h5">{payment_dict.member_name}</Typography> */}
              <Typography variant="h6">{payment_dict.member_name}</Typography>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {payment_dict.discount > 0 && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: "text.disabled",
                      textDecoration: "line-through",
                    }}
                  >
                    {formatCurrency(payment_dict.price)}
                  </Typography>
                )}
                <Typography fontWeight={600} color="success.main">
                  {formatCurrency(payment_dict.amount)}
                </Typography>
              </Box>
            </Box>
          }
          // action={
          //   <IconButton onClick={() => handleEditClick(payment_dict)} size="small">
          //     <EditTwoToneIcon fontSize="small" />
          //   </IconButton>
          // }
          sx={{
            px: 2, py: 0, alignItems: 'center',
            '& .MuiCardHeader-action': { alignSelf: 'center' },
          }}
          slotProps={{
            content: { sx: { py: 1.5 } },
          }}
        />

        <Divider />

        {/* Main Content */}
        <CardContent sx={{ pt: 1.5, pb: 1, flexGrow: 1 }}>
          <Stack spacing={0.75}>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Date</Typography>
              <Typography variant="body2">{humanReadableDate(payment_dict.payment_date) ?? NA_LABEL}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Mode</Typography>
              <Typography variant="body2">{titleCase(payment_dict.payment_mode) ?? NA_LABEL}</Typography>
            </Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="body2" color="text.secondary">Status</Typography>
              <Chip
                size="small"
                label={titleCase(payment_dict.status)}
                color={chipColor}
                sx={{ height: 20, fontSize: 11 }}
              />
            </Box>
          </Stack>
        </CardContent>

        {/* Collapsible Content */}
        <Collapse in={expanded}>
          <Divider sx={{ borderStyle: 'dashed' }} />
          <CardContent sx={{ bgcolor: '#FBFBFB', pt: 1.5, pb: 1 }}>
            <Stack spacing={0.75}>
              {payment_dict.line_items?.map((item, i) => (
                <Box>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      {FITNESS_PAYMENT_ITEM_TYPE_LABEL[item.item_type] ?? item.item_type}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      {item.discount > 0 && (
                        <Typography variant="body2" sx={{ color: "text.disabled", textDecoration: "line-through" }}>
                          {formatCurrency(item.price)}
                        </Typography>
                      )}
                      <Typography variant="body2" color="text.secondary">
                        {formatCurrency(item.amount)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              ))}
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Total
                </Typography>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  {payment_dict.discount > 0 && (
                    <Typography variant="body2" sx={{ color: "text.disabled", textDecoration: "line-through" }}>
                      {formatCurrency(payment_dict.price)}
                    </Typography>
                  )}
                  <Typography variant="body2" color="text.secondary">
                    {formatCurrency(payment_dict.amount)}
                  </Typography>
                </Box>
              </Box>
            </Stack>
          </CardContent>
        </Collapse>

        {/* Expand toggle — full width bottom */}
        {/* <Divider /> */}
        <Box
          onClick={handleExpandClick}
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            py: 0.25,
            cursor: 'pointer',
            '&:hover': { bgcolor: 'action.hover' },
          }}
        >
          <KeyboardDoubleArrowDownIcon
            fontSize="small"
            sx={{
              color: 'text.secondary',
              transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.3s',
            }}
          />
        </Box>
      </Card>
    );
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 64px - 64px)', // viewport height - margings - app bar
      }}
    >
      {/* Title and Button (fixed) */}
      <Box
        sx={{
          flexShrink: 0, // Top level space distribution
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Title */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            mt: 1,
            mb: 3,
            gap: 2,
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Title>{TITLE}</Title>
          </Box>
          {/* Search Bar */}
          <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
            {/* <Button
              variant="contained"
              startIcon={<AddCircleIcon />}
              onClick={handleAddClick}
              sx={{
                px: 4,
              }}
            >
              Add
            </Button> */}
          </Box>
        </Box>
      </Box>

      {/* Scrollable table container */}
      <Box
        sx={{
          flexGrow: 1, // This makes the table container grow and take the available space
          overflowY: 'auto', // Only the table will scroll
          // mt: 2,
          minHeight: 200, // Ensures proper space for loader
          display: 'flex',
          flexDirection: 'column', // Aligns table to the top when shown
          alignItems: isLoading ? 'center' : 'stretch', // Center loader, but stretch table
          justifyContent: isLoading ? 'center' : 'flex-start', // Center loader, start table at top,
        }}
      >
        {isLoading ? (
          <Fade in={isLoading} timeout={500}>
            <CircularProgress size={50} thickness={3} />
          </Fade>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            {rows.length === 0 ? (
              // Display message when there are no rows
              <Box
                sx={{
                  textAlign: 'left',
                }}
              >
                <Typography variant="h5">{NO_DATA_AVAILABLE_STRING}</Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  width: '100%',
                  py: 2,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    md: 'repeat(2, 1fr)',
                    xl: 'repeat(3, 1fr)',
                  },
                  gap: 1.5, // This creates even spacing without edge padding
                }}
              >
                {filteredRows.map((item_dict, index) => (
                  <Box key={index}>
                    <PaymentCard
                      payment_row={index}
                      payment_dict={item_dict}
                      relatedData={{}}
                    />
                  </Box>
                ))}
              </Box>
            )}
          </Fade>
        )}
      </Box>      
    </Box>
  );
}
